import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import type { FieldDataType, FieldValidation } from '@/types/inventory'

interface FieldRulesEditorProps {
  dataType: FieldDataType
  validation: FieldValidation
  onValidationChange: (next: FieldValidation) => void
  /** Only meaningful for `enum` — the selectable options, stored separately from the rules. */
  options: string[]
  onOptionsChange: (next: string[]) => void
}

function Num({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: number | undefined
  onChange: (v: number | undefined) => void
  placeholder?: string
}) {
  return (
    <label className='flex items-center gap-1.5 text-xs text-gray-600'>
      {label}
      <Input
        type='number'
        value={value === undefined ? '' : String(value)}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value.trim()
          onChange(raw === '' ? undefined : Number(raw))
        }}
        className='h-7 w-20'
      />
    </label>
  )
}

/**
 * The per-type rule panel shown when defining a field. Only the rules that apply
 * to the selected `dataType` are offered — the backend rejects any key that
 * doesn't belong to the type, so surfacing them all would invite 400s.
 */
export function FieldRulesEditor({
  dataType,
  validation,
  onValidationChange,
  options,
  onOptionsChange,
}: FieldRulesEditorProps) {
  const [newOption, setNewOption] = useState('')
  const set = (patch: Partial<FieldValidation>) => onValidationChange({ ...validation, ...patch })

  function addOption() {
    const v = newOption.trim()
    if (!v || options.includes(v)) return
    onOptionsChange([...options, v])
    setNewOption('')
  }

  return (
    <div className='w-full space-y-2 rounded-lg border border-gray-200 bg-white p-2.5'>
      <div className='flex items-center justify-between'>
        <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
          Rules for this {dataType} field
        </span>
        <label className='flex items-center gap-1.5 text-xs text-gray-600'>
          Required
          <Switch checked={!!validation.required} onCheckedChange={(v) => set({ required: v || undefined })} />
        </label>
      </div>

      {dataType === 'number' && (
        <div className='flex flex-wrap items-center gap-3'>
          <Num label='Min' value={validation.min} onChange={(v) => set({ min: v })} />
          <Num label='Max' value={validation.max} onChange={(v) => set({ max: v })} />
          <label className='flex items-center gap-1.5 text-xs text-gray-600'>
            Whole numbers only
            <Switch checked={!!validation.integer} onCheckedChange={(v) => set({ integer: v || undefined })} />
          </label>
        </div>
      )}

      {dataType === 'string' && (
        <div className='flex flex-wrap items-center gap-3'>
          <Num label='Max length' value={validation.maxLength} onChange={(v) => set({ maxLength: v })} />
          <label className='flex flex-1 items-center gap-1.5 text-xs text-gray-600'>
            Pattern
            <Input
              value={validation.pattern ?? ''}
              placeholder='e.g. ^[A-Z]{2}-\d+$'
              onChange={(e) => set({ pattern: e.target.value.trim() || undefined })}
              className='h-7 flex-1 font-mono text-xs'
            />
          </label>
        </div>
      )}

      {dataType === 'date' && (
        <div className='flex flex-wrap items-center gap-3'>
          <label className='flex items-center gap-1.5 text-xs text-gray-600'>
            Earliest
            <Input
              type='date'
              value={validation.minDate?.slice(0, 10) ?? ''}
              onChange={(e) => set({ minDate: e.target.value || undefined })}
              className='h-7 w-36'
            />
          </label>
          <label className='flex items-center gap-1.5 text-xs text-gray-600'>
            Latest
            <Input
              type='date'
              value={validation.maxDate?.slice(0, 10) ?? ''}
              onChange={(e) => set({ maxDate: e.target.value || undefined })}
              className='h-7 w-36'
            />
          </label>
        </div>
      )}

      {dataType === 'enum' && (
        <div className='space-y-1.5'>
          <div className='flex flex-wrap gap-1.5'>
            {options.map((o) => (
              <span
                key={o}
                className='flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700'
              >
                {o}
                <button
                  type='button'
                  aria-label={`Remove option ${o}`}
                  onClick={() => onOptionsChange(options.filter((x) => x !== o))}
                  className='text-gray-400 hover:text-red-500'
                >
                  <X className='h-3 w-3' />
                </button>
              </span>
            ))}
            {options.length === 0 && (
              <span className='text-xs italic text-muted-foreground'>
                No options yet — a select field needs at least one.
              </span>
            )}
          </div>
          <div className='flex items-center gap-1.5'>
            <Input
              value={newOption}
              placeholder='Add an option'
              onChange={(e) => setNewOption(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addOption()
                }
              }}
              className='h-7 w-40 text-xs'
            />
            <button
              type='button'
              onClick={addOption}
              className='flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
              aria-label='Add option'
            >
              <Plus className='h-3.5 w-3.5' />
            </button>
            <label className='ml-2 flex items-center gap-1.5 text-xs text-gray-600'>
              Allow multiple
              <Switch checked={!!validation.multiple} onCheckedChange={(v) => set({ multiple: v || undefined })} />
            </label>
          </div>
        </div>
      )}

      {dataType === 'file' && (
        <div className='space-y-1.5'>
          <div className='flex flex-wrap items-center gap-3'>
            <label className='flex flex-1 items-center gap-1.5 text-xs text-gray-600'>
              Accepted types
              <Input
                value={(validation.accept ?? []).join(', ')}
                placeholder='.pdf, .png'
                onChange={(e) => {
                  const list = e.target.value
                    .split(',')
                    .map((s) => s.trim().toLowerCase())
                    .filter(Boolean)
                  set({ accept: list.length ? list : undefined })
                }}
                className='h-7 flex-1 text-xs'
              />
            </label>
            <Num label='Max MB' value={validation.maxSizeMb} onChange={(v) => set({ maxSizeMb: v })} />
          </div>
          <p className='text-[11px] italic text-muted-foreground'>
            File upload isn’t available yet — these rules are stored and enforced, but only the
            file’s name is recorded against a material.
          </p>
        </div>
      )}

      {dataType === 'boolean' && (
        <p className='text-xs text-muted-foreground'>A yes/no field has no further rules.</p>
      )}
    </div>
  )
}
